import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { useModal } from '@hooks/app/modal.hook'

import { Modal } from '@components/Modal'
import { Input } from '@components/Input'
import { Refresh } from '@components/Refresh'

import { useUpdateSequenceTemplate } from '@hooks/sequence-template/update-sequence-template.hook'
import { useSequenceTemplate } from '@hooks/sequence-template/sequence-template.hook'

import { Container, HeaderContainer, SequenceTemplatePlanningContainer } from './edit-sequence-template.styled'

import { SequenceTaskTemplatePlanningType, SequenceTemplatePlanning } from '../../components/SequenceTemplatePlanning'

export const EditSequenceTemplatePage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const sequenceId = searchParams.get('sequenceId')

  const { data, loading, error, refetch } = useSequenceTemplate(sequenceId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const sequence = data?.sequenceTemplate

  const [name, setName] = useState<string>('')
  const [sequenceTaskTemplates, setSequenceTaskTemplates] = useState<SequenceTaskTemplatePlanningType[]>([])

  const { updateSequenceTemplate } = useUpdateSequenceTemplate()

  useEffect(() => {
    if (sequence) {
      setName(sequence.name)

      setSequenceTaskTemplates(
        sequence.tasks.map((st) => ({
          id: st.id,
          name: st.name,
          description: st.description,
          startDay: st.startDay,
          startRow: st.startRow,
          duration: st.duration,
          workforce: st.workforce,
          tradeKey: st.trade.key,
          trade: st.trade,
          companyId: st.company?.id,
          company: st.company,
        })),
      )
    }
  }, [sequence])

  return (
    <Modal
      fullScreen
      title={t('modules_project-settings_pages_edit_sequence_template_title')}
      onSubmitLabel={t('modules_project-settings_pages_edit_sequence_template_button_edit-sequence')}
      onClose={() => {
        hideModal('update_sequence', { sequenceId })
      }}
      onSubmit={() => {
        if (sequenceId) {
          updateSequenceTemplate({
            id: sequenceId,
            name,
            description: '',
            tasks: sequenceTaskTemplates.map((st) => ({
              name: st.name,
              startDay: st.startDay,
              startRow: st.startRow,
              duration: st.duration,
              workforce: st.workforce,
              tradeKey: st.tradeKey,
              companyId: st.companyId,
            })),
          })

          hideModal('update_sequence', { sequenceId })
        }
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(sequence?.id || '')}>
          <HeaderContainer>
            <Input
              className="input-sequence-name"
              autoFocus
              required
              label={t('modules_project-settings_pages_edit_sequence_template_label_sequence-name')}
              placeholder={t('modules_project-settings_pages_edit_sequence_template_placeholder_sequence-name')}
              value={name}
              onChange={(v) => setName(v)}
            />

            <div style={{ flex: 1 }} />
          </HeaderContainer>

          {useMemo(
            () => (
              <SequenceTemplatePlanningContainer>
                <SequenceTemplatePlanning
                  sequenceTaskTemplates={sequenceTaskTemplates}
                  setSequenceTaskTemplates={setSequenceTaskTemplates}
                />
              </SequenceTemplatePlanningContainer>
            ),
            [sequenceTaskTemplates],
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
